import React from 'react';
import { Sparkles, Camera, Zap } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import modalImage from '@/img/modal.jpg';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignIn: () => void;
  onSignUp: () => void;
  onContinueFree: () => void;
}

export default function WelcomeModal({ isOpen, onClose, onSignIn, onSignUp, onContinueFree }: WelcomeModalProps) {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-0 md:p-4">
      {/* Backdrop - No click to close */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      {/* Modal - Mobile: full screen scrollable, Desktop: centered with max-width */}
      <div className="relative bg-bg-panel rounded-none md:rounded-2xl shadow-2xl w-full h-full md:h-auto md:max-w-5xl md:mx-4 overflow-y-auto flex flex-col md:flex-row md:max-h-[90vh]">
        {/* Image - Mobile: top (scrolls), Desktop: left (fixed) */}
        <div className="w-full md:w-1/2 relative">
          <img 
            src={modalImage} 
            alt="Create stunning visuals with AI" 
            className="w-full h-auto object-cover"
          />
        </div>

        {/* Content - Scrolls together with image on mobile */}
        <div className="w-full md:w-1/2 p-6 md:p-12 relative">
          {/* Content */}
          <div>
            <h2 className="text-3xl font-bold text-text-primary mb-6">{t('welcome.title')}</h2>
            
            <p className="text-text-secondary mb-8">
              {t('welcome.description')}
            </p>

            {/* Features */}
            <div className="space-y-4 mb-10">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Camera className="w-4 h-4 text-accent" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-text-primary mb-1">{t('welcome.feature1Title')}</h4>
                  <p className="text-xs text-text-secondary">{t('welcome.feature1Desc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-accent" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-text-primary mb-1">{t('welcome.feature2Title')}</h4>
                  <p className="text-xs text-text-secondary">{t('welcome.feature2Desc')}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Zap className="w-4 h-4 text-accent" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-text-primary mb-1">{t('welcome.feature3Title')}</h4>
                  <p className="text-xs text-text-secondary">{t('welcome.feature3Desc')}</p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col gap-3">
            <button
                onClick={onSignUp}
                className="w-full px-6 py-3 bg-accent text-bg-main rounded-full text-sm font-semibold hover:bg-accent-hover transition-all "
              >
                {t('auth.createAccount')}
              </button>
              <button
                onClick={onSignIn}
                className=" w-full px-6 py-3 bg-bg-control text-text-primary rounded-full text-sm font-medium hover:bg-bg-control-hover transition-all"
              >
                {t('auth.signIn')}
              </button>

              <button
                onClick={onContinueFree}
                className="w-full px-6 py-3 text-text-secondary rounded-full text-sm font-medium hover:text-text-primary transition-all"
              >
                {t('welcome.continueWithout')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

